<script setup lang="ts">
const { formatDate } = useFormatDate()

const { data: page } = await useAsyncData('index', () => queryCollection('index').first())

const { data: latestPosts } = await useAsyncData('index-latest-posts', () =>
  queryCollection('posts')
    .where('published', '=', true)
    .order('date', 'DESC')
    .limit(3)
    .all()
)

const title = page.value?.seo?.title || page.value?.title
const description = page.value?.seo?.description || page.value?.description

useSeoMeta({
  titleTemplate: '',
  title,
  ogTitle: title,
  description,
  ogDescription: description
})

// Nicht alle Stimmen sind tatsächlich vom Web-Developer-Lehrgang (z. B. Benjamin
// v. G.: "Kursteilnehmer PHP Kurs") – itemReviewed nur setzen, wenn die Rolle das
// auch bestätigt, sonst würde das Schema eine Bewertung behaupten, die es nicht
// gibt (siehe „W5" in docs/offene-punkte-webpro-llm.md). Kein `aggregateRating`,
// da keine Einzelbewertungen mit Zahlenwert hinterlegt sind (task-llm-optimierung-
// webpro.md P3).
useJsonLd(() => page.value?.testimonials?.items?.length
  ? {
      '@graph': page.value.testimonials.items.map(testimonial => ({
        '@type': 'Review',
        'reviewBody': testimonial.quote,
        'author': { '@type': 'Person', 'name': testimonial.user.name },
        ...(/Web Developer Lehrgang/i.test(testimonial.user.description)
          ? { itemReviewed: { '@id': `${absoluteUrl('/lehrgang/developer')}/#course` } }
          : {})
      }))
    }
  : null, 'testimonials-schema')
</script>

<template>
  <div v-if="page">
    <UPageHero
      :title="page.title"
      :description="page.description"
    >
      <template #links>
        <!-- h-10: feste Höhe statt der aus den Font-Metriken abgeleiteten
             Zeilenhöhe. Das war der tatsächliche Layout-Shift-Verursacher
             (nicht die Feature-Karten weiter unten, wie zuerst vermutet) –
             Public Sans und die Fallback-Schrift ergeben pro Button eine leicht
             unterschiedliche Höhe, was sich über vier gestapelte Buttons auf
             Mobile zu einem messbaren Sprung summiert. Siehe Runde 9 in
             docs/offene-punkte-webpro-llm.md. -->
        <UButton
          v-for="link in page.hero.links"
          :key="link.to"
          v-bind="link"
          class="w-full sm:w-auto justify-center h-10"
        />
      </template>

      <template #top>
        <HeroBackground />
      </template>

      <template #title>
        <MDC
          :value="page.title"
          unwrap="p"
        />
      </template>
      <!-- <PromotionalVideo /> -->
    </UPageHero>

    <UPageSection
      v-for="(section, index) in page.sections"
      :key="index"
      :title="section.title"
      :description="section.description"
      :orientation="section.orientation"
      :reverse="section.reverse"
      :features="section.features"
    >
      <NuxtImg
        src="camps_friends/camp03.webp"
        alt="Zwei Teilnehmende arbeiten nebeneinander an ihren Laptops bei einem Web Pro & Friends Barcamp"
        class="w-full rounded-lg shadow-xl"
      />
    </UPageSection>

    <UPageSection
      :title="page.features.title"
      :description="page.features.description"
    >
      <UPageGrid>
        <UPageCard
          v-for="(item, index) in page.features.items"
          :key="index"
          v-bind="item"
          spotlight
        >
          <template #title>
            <!-- min-h-14 reserviert Platz für zwei Zeilen, unabhängig davon, ob
                 gerade die Web-Font oder die (metrik-angepasste, aber nicht
                 pixelgleiche) Fallback-Schrift aktiv ist – verhindert den
                 Layout-Shift an der Wurzel, statt sich auf font-display allein
                 zu verlassen (siehe Runde 9 in docs/offene-punkte-webpro-llm.md). -->
            <h3 class="text-xl font-semibold min-h-14 flex items-center">
              {{ item.title }}
            </h3>
          </template>

          <template
            v-if="item.to"
            #footer
          >
            <UButton
              label="Entdecke die Kurse"
              trailing-icon="i-lucide-arrow-right"
              variant="subtle"
              size="sm"
              tabindex="-1"
            />
          </template>
        </UPageCard>
      </UPageGrid>
    </UPageSection>

    <UPageSection
      id="upcoming"
      title="Aktuelle Kurse und Veranstaltungen"
      description="Die nächsten Termine im Überblick – Kurse, Events und Veranstaltungen."
    >
      <UContainer class="max-w-4xl">
        <UpcomingList :limit="10" />
      </UContainer>
    </UPageSection>

    <UPageSection
      v-if="latestPosts?.length"
      id="latest-posts"
      title="Aktuelles aus dem Blog"
      description="Die letzten Beiträge – Hintergründe, Stimmen und Neuigkeiten aus der Web Professionals Community."
    >
      <UBlogPosts>
        <UBlogPost
          v-for="post in latestPosts"
          :key="post.path"
          :to="post.path"
          :title="post.title"
          :description="post.description"
          :image="post.image"
          :date="post.date"
          :authors="post.authors"
          variant="naked"
          :ui="{ description: 'line-clamp-2' }"
        >
          <template
            v-if="post.badge?.label?.length"
            #badge
          >
            <div class="flex flex-wrap items-center gap-1">
              <UBadge
                v-for="label in post.badge.label.slice(0, 2)"
                :key="label"
                :label="label"
                variant="subtle"
              />
            </div>
          </template>
          <template #date>
            {{ formatDate(post.date) }}
          </template>
        </UBlogPost>
      </UBlogPosts>

      <div class="flex justify-center mt-6">
        <UButton
          to="/blog"
          variant="subtle"
          trailing-icon="i-lucide-arrow-right"
          label="Alle Beiträge ansehen"
        />
      </div>
    </UPageSection>

    <UPageSection
      id="testimonials"
      :description="page.testimonials.description"
    >
      <template #title>
        <span
          v-if="page.testimonials.headline"
          class="block text-sm font-semibold text-primary mb-1"
        >{{ page.testimonials.headline }}</span>
        {{ page.testimonials.title }}
      </template>

      <UPageColumns class="xl:columns-4">
        <UPageCard
          v-for="(testimonial, index) in page.testimonials.items"
          :key="index"
          variant="subtle"
          :description="testimonial.quote"
          :ui="{ description: 'before:content-[open-quote] after:content-[close-quote]' }"
        >
          <template #footer>
            <UUser
              v-bind="testimonial.user"
              size="lg"
            />
          </template>
        </UPageCard>
      </UPageColumns>
    </UPageSection>

    <USeparator />

    <UPageCTA
      v-bind="page.cta"
      variant="naked"
      class="overflow-hidden"
    >
      <LazyStarsBg />
    </UPageCTA>
  </div>
</template>
